import React from 'react';
import { Plugin, Template, TemplatePlaceholder } from '@devexpress/dx-react-core';
import { Box } from '@mui/material';

import { loadAllDataForProject } from '../../../actions/loadAllDataForProject';
import { getProjectLayoutModels } from '../../../selectors/getProjectLayoutModels';
import { getHistoryGridCheckedBundleGuids, getHistoryGridRows } from '../../../selectors/historyGrid';
import { isGroupedProductionComponentInstanceRow } from '../../../selectors/scheduleGroupGrid';
import { collapseRows, setProjectGuidFilter } from '../../../store/historyGridSlice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { CollapseAllButton } from '../CollapseAllButton';
import { DownloadProductionPackageButton } from '../DownloadProductionPackageButton';
import { ProjectSelection } from '../ProjectSelection';
import { SetStatus } from '../SetStatus';

export function HistoryGridToolbarPlugin() {
  const dispatch = useAppDispatch();
  const checkedBundleGuids = useAppSelector(getHistoryGridCheckedBundleGuids);
  const projectGuidFilter = useAppSelector((state) => state.historyGrid.projectGuidFilter);
  const projects = useAppSelector(getProjectLayoutModels);

  const historyGridRows = useAppSelector(getHistoryGridRows);
  const allExpandableOrCollapsableRowIds = historyGridRows
    .filter((row) => !isGroupedProductionComponentInstanceRow(row.rowId))
    .map((row) => row.rowId);

  function onSelectProjectFilter(projectGuid: string | null) {
    dispatch(setProjectGuidFilter(projectGuid));

    if (projectGuid === null) return;
    // When filtering to a project, load the components for each of the production groups associated with that project
    // so that filtering for the component names will work
    dispatch(loadAllDataForProject(projectGuid));
  }

  return (
    <Plugin name="HistoryGridToolbar" dependencies={[{ name: 'Toolbar' }]}>
      <Template name="toolbarContent">
        <Box sx={{ display: 'flex', flexDirection: 'row', verticalAlign: 'middle' }}>
          <Box sx={{ width: 600, p: 1 }}>
            <ProjectSelection
              activeProjectGuid={projectGuidFilter}
              projects={projects}
              setActiveProjectGuid={onSelectProjectFilter}
            />
          </Box>

          <Box sx={{ width: 400, p: 1 }}>
            <SetStatus
              checkedBundleGuids={checkedBundleGuids}
              options={['Scheduled', 'StartedAssembly', 'FinishedAssembly', 'StoredOnYard', 'Shipped']}
            />
          </Box>

          <Box sx={{ p: 1 }}>
            <DownloadProductionPackageButton
              bundleGuids={checkedBundleGuids}
              availableOutputTypes={['ProductionAndShippingList', 'ComponentReport']}
              productionArtifactsContext="ScheduleGroup"
            />
          </Box>
        </Box>
        <TemplatePlaceholder />
        <CollapseAllButton
          collapseAll={(rowIds) => dispatch(collapseRows(rowIds))}
          allCollapsableRowIds={allExpandableOrCollapsableRowIds}
        />
      </Template>
    </Plugin>
  );
}
